import type { Key } from "./hashing";

export interface SpaceSavingOptions {
  /** Number of items to monitor. More slots → more items tracked accurately. */
  capacity: number;
}

export interface Counter {
  key: string;
  /** Estimated count — an upper bound on the true count. */
  count: number;
  /** Maximum overestimation: the true count lies in [count − error, count]. */
  error: number;
}

/**
 * Space-Saving (Metwally, Agrawal & El Abbadi) finds the *top-K* most frequent
 * items in a stream — not the frequency of an item you name (that's Count-Min),
 * but *which* items are hot — using only K counters.
 *
 * It monitors at most `capacity` items. A new item that isn't monitored evicts
 * the current minimum: it takes that slot with count = min + 1 and remembers the
 * evicted count as its possible error. The guarantee: any item whose true
 * frequency exceeds N / capacity is definitely being monitored, and every
 * counter's true value lies in [count − error, count].
 */
export class SpaceSaving {
  readonly capacity: number;
  private readonly counters = new Map<string, Counter>();
  total = 0;

  constructor(options: SpaceSavingOptions) {
    if (!Number.isInteger(options.capacity) || options.capacity <= 0)
      throw new RangeError("capacity must be a positive integer");
    this.capacity = options.capacity;
  }

  add(key: Key, weight = 1): void {
    const k = typeof key === "string" ? key : new TextDecoder().decode(key);
    this.total += weight;

    const existing = this.counters.get(k);
    if (existing) {
      existing.count += weight;
      return;
    }
    if (this.counters.size < this.capacity) {
      this.counters.set(k, { key: k, count: weight, error: 0 });
      return;
    }
    // Evict the current minimum and hand its slot to the new item.
    const min = this.minCounter();
    this.counters.delete(min.key);
    this.counters.set(k, { key: k, count: min.count + weight, error: min.count });
  }

  private minCounter(): Counter {
    let min: Counter | null = null;
    for (const c of this.counters.values()) {
      if (min === null || c.count < min.count) min = c;
    }
    return min!;
  }

  /** The top-k monitored items by estimated count, highest first. */
  topK(k: number): Counter[] {
    return [...this.counters.values()]
      .sort((a, b) => b.count - a.count)
      .slice(0, k)
      .map((c) => ({ ...c }));
  }

  /** Estimated (upper-bound) count for a key; 0 if not monitored. */
  estimate(key: Key): number {
    const k = typeof key === "string" ? key : new TextDecoder().decode(key);
    return this.counters.get(k)?.count ?? 0;
  }

  /** Items with true frequency above this are guaranteed to be monitored. */
  guaranteedThreshold(): number {
    return this.total / this.capacity;
  }

  /** Number of items currently monitored (≤ capacity). */
  size(): number {
    return this.counters.size;
  }
}
